import React, { useEffect, useState, useRef, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';

import './desktop-wish-list-style.scss';
import { CredentialContext, credentialType } from '@providers';
import useIsComDomain from '@src/hooks/useIsComDomain';

import LoveImg from '@src/imagesNew/icons/love.png';

import Button from '@src/componentsNew/Buttons/Button';
import Typography from '@src/componentsNew/Typography/Typography';
import GlobalStoreInfo from '@src/componentsNew/GlobalStore/GlobalStoreInfo';
import DesktopWishListProductThumbnail from '@src/componentsNew/ProductThumbnail/WishListProductThumbnail/DesktopWishListProductThumbnail';
import DesktopProductListSkeleton from '@src/componentsNew/Skeleton/desktop/components/DesktopProductListSkeleton';

import { ME_FOR_WISHLIST } from '@src/pages/WishList/queries';
import { meForWishlist, meForWishlistVariables } from '@src/pages/WishList/__generated__/meForWishlist';
import { ME_FOR_NOTIFY_ME } from '@molecules/NotifyMeModal/queries';
import { meForNotifyMe } from '@molecules/NotifyMeModal/__generated__/meForNotifyMe';
import { baseUrl } from '@src/app/routes';

const DesktopWishListPage = () => {
  const history = useHistory();
  const isComDomain = useIsComDomain();
  const { currentUser, setCurrentUser } = useContext<credentialType>(CredentialContext);

  const [variables] = useState<meForWishlistVariables>({
    first: 12,
    // Domain Revert Comment
    // forMgs: isComDomain ? true : false
  });

  const fetchingRef = useRef(false);
  const hasNextPageRef = useRef(false);
  const prevProductsNum = useRef(0);

  const { data: meData } = useQuery<meForNotifyMe>(ME_FOR_NOTIFY_ME, {
    onCompleted: (data) => {
      const mobileNumber = data?.me?.mobileNumber;
      const mobileVerified = data?.me?.mobileVerified;

      const copyCurrentUser = JSON.parse(JSON.stringify(currentUser));
      copyCurrentUser.user.mobileNumber = Number(mobileNumber);
      copyCurrentUser.user.mobileVerified = mobileVerified;

      // local user info update
      setCurrentUser(copyCurrentUser);
    },
  });

  const { data, loading, refetch, fetchMore } = useQuery<meForWishlist>(ME_FOR_WISHLIST, {
    variables,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const wishlist = data?.me?.wishlist?.items;

      hasNextPageRef.current = wishlist?.pageInfo?.hasNextPage;
      prevProductsNum.current = wishlist?.edges?.length;
    },
  });

  const handleOnRefetch = async () => {
    const refetchData = await refetch();

    hasNextPageRef.current = refetchData?.data?.me?.wishlist?.items?.pageInfo?.hasNextPage;
    prevProductsNum.current = refetchData?.data?.me?.wishlist?.items?.edges?.length;
  };

  useEffect(() => {
    const handleOnScroll = async () => {
      const scrollTop = (document.documentElement && document.documentElement.scrollTop) || document.body.scrollTop;
      const scrollHeight =
        (document.documentElement && document.documentElement.scrollHeight) || document.body.scrollHeight;
      const clientHeight = document.documentElement.clientHeight || window.innerHeight;
      const scrolledToBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight * 0.75;

      if (scrolledToBottom && hasNextPageRef.current && !fetchingRef.current) {
        fetchingRef.current = true;

        const newVariables = {
          ...variables,
          after: data?.me?.wishlist?.items?.pageInfo?.endCursor,
        };

        if (prevProductsNum.current !== data?.me?.wishlist?.items?.edges.length) {
          fetchingRef.current = false;

          return;
        }

        await fetchMore({
          variables: newVariables,
          updateQuery: (prevResult: meForWishlist, { fetchMoreResult }) => {
            const wishlist = fetchMoreResult?.me?.wishlist?.items;
            const newEdges = wishlist?.edges;
            const pageInfo = wishlist?.pageInfo;
            const totalCount = wishlist?.totalCount;

            hasNextPageRef.current = pageInfo?.hasNextPage;
            prevProductsNum.current += newEdges?.length;

            return newEdges.length
              ? {
                  me: {
                    ...prevResult.me,
                    wishlist: {
                      ...prevResult.me.wishlist,
                      items: {
                        ...prevResult.me.wishlist.items,
                        edges: [...prevResult.me.wishlist.items.edges, ...newEdges],
                        pageInfo,
                        totalCount,
                      },
                    },
                  },
                }
              : prevResult;
          },
        });

        fetchingRef.current = false;
      }
    };

    document.addEventListener('scroll', handleOnScroll);
    return () => {
      document.removeEventListener('scroll', handleOnScroll);
    };
  }, [data]);

  if (loading) {
    return (
      <div style={{ paddingTop: '102px' }}>
        <DesktopProductListSkeleton type="horizontal" count={12} />
      </div>
    );
  }

  return (
    <div
      id="desktop-wish-list-page-container"
      className={`${data?.me?.wishlist?.items?.totalCount > 0 ? 'non-empty' : 'empty'}`}
    >
      {/* Domain Revert Comment */}
      {/* {
        (!isComDomain && data?.me?.wishlist?.mgsTypeItems?.totalCount > 0) ?
          <GlobalStoreInfo isMobile={false} itemCount={data?.me?.wishlist?.mgsTypeItems?.totalCount} infoPage="wishlist" />
          : null
      } */}

      {data?.me?.wishlist?.items?.totalCount > 0 ? (
        <div className="wishlist-thumbnail-wrapper">
          {data?.me?.wishlist?.items?.edges?.map((edge) => {
            const wishlistProduct = edge.node.product;

            return (
              <DesktopWishListProductThumbnail
                key={wishlistProduct.id}
                refetch={handleOnRefetch}
                wishlistProduct={wishlistProduct}
                productId={wishlistProduct.id}
              />
            );
          })}
        </div>
      ) : (
        <div className="desktop-empty-wish-list-wrapper">
          <img src={LoveImg} width={150} alt="empty wish list" />

          <div className="content-wrapper">
            <div className="text-wrapper">
              <Typography cName="empty-main-sentence" variant="subtitle1" component="p" style={{ color: '#090A0FDE' }}>
                Save Your Fave Products!
              </Typography>

              <Typography cName="empty-main-sentence" variant="body2" component="p" style={{ color: '#11141D99' }}>
                Would you like to save interesting product to fave?
              </Typography>
            </div>

            <Button size="medium" variant="contained" onClick={() => history.push(baseUrl)}>
              Browse Products
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopWishListPage;
